import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import usePlanFeatures from '../hooks/usePlanFeatures';
import {
  MessageSquare, Save, Loader2, AlertTriangle, ToggleLeft, ToggleRight,
  CheckCircle, XCircle, Info, Lock,
} from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// NetGSM kurulumu tamamlandığında false yap
const SMS_COMING_SOON = true;

const SMS_TYPES = ['reminder', 'confirmed', 'canceled', 'satisfaction'];

const DEFAULT_TEMPLATES = {
  reminder: 'Merhaba {customerName}, {businessName} salonunda {date} tarihinde saat {time} randevunuzu hatırlatırız.',
  confirmed: 'Merhaba {customerName}, {businessName} salonundaki {date} / {time} randevunuz onaylandı.',
  canceled: 'Merhaba {customerName}, {businessName} salonundaki {date} / {time} randevunuz iptal edildi.',
  satisfaction: 'Merhaba {customerName}, {businessName} salonundan aldığınız hizmetten memnun kaldınız mı? Değerlendirmeniz için: {reviewLink}',
};

export default function SmsSettings() {
  const { t } = useTranslation('settings');
  const { user } = useAuth();
  const { isFeatureAllowed, smsMonthlyLimit } = usePlanFeatures();
  const isSmsAllowed = isFeatureAllowed('sms');

  const [loading, setLoading] = useState(true);
  const [smsConfig, setSmsConfig] = useState({
    apiUser: '',
    apiKey: '',
    senderTitle: '',
    isActive: false,
    usedThisMonth: 0,
    resetAt: '',
  });
  const [savingConfig, setSavingConfig] = useState(false);

  const [smsTemplates, setSmsTemplates] = useState({ ...DEFAULT_TEMPLATES });
  const [savingTemplate, setSavingTemplate] = useState({});

  useEffect(() => {
    if (!user?.businessId) return;
    const load = async () => {
      try {
        const [bizSnap, ...tmplSnaps] = await Promise.all([
          getDoc(doc(db, 'businesses', user.businessId)),
          ...SMS_TYPES.map((type) =>
            getDoc(doc(db, 'sms_templates', `${user.businessId}_${type}`))
          ),
        ]);

        if (bizSnap.exists() && bizSnap.data().smsConfig) {
          setSmsConfig((prev) => ({ ...prev, ...bizSnap.data().smsConfig }));
        }

        const tmplMap = { ...DEFAULT_TEMPLATES };
        tmplSnaps.forEach((snap, i) => {
          if (snap.exists() && snap.data().template) {
            tmplMap[SMS_TYPES[i]] = snap.data().template;
          }
        });
        setSmsTemplates(tmplMap);
      } catch {
        toast.error(t('settings.loadError'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.businessId]);

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await updateDoc(doc(db, 'businesses', user.businessId), {
        'smsConfig.apiUser': smsConfig.apiUser.trim(),
        'smsConfig.apiKey': smsConfig.apiKey.trim(),
        'smsConfig.senderTitle': smsConfig.senderTitle.trim().slice(0, 11),
        'smsConfig.isActive': smsConfig.isActive,
        updatedAt: serverTimestamp(),
      });
      toast.success(t('settings.sms.saveSuccess'));
    } catch (err) {
      toast.error(t('settings.sms.saveError') + ': ' + err.message);
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSaveTemplate = async (type) => {
    setSavingTemplate((prev) => ({ ...prev, [type]: true }));
    try {
      await setDoc(doc(db, 'sms_templates', `${user.businessId}_${type}`), {
        businessId: user.businessId,
        type,
        template: smsTemplates[type],
        isActive: true,
        updatedAt: serverTimestamp(),
      });
      toast.success(t('settings.sms.templateSaveSuccess'));
    } catch (err) {
      toast.error(t('settings.sms.templateSaveError') + ': ' + err.message);
    } finally {
      setSavingTemplate((prev) => ({ ...prev, [type]: false }));
    }
  };

  if (SMS_COMING_SOON) {
    return (
      <div className="max-w-2xl mx-auto py-16 px-4 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-5">
          <Lock className="h-7 w-7 text-amber-500" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">SMS Entegrasyonu</h1>
        <p className="text-gray-500 mb-1">Bu özellik yakında kullanıma açılacak.</p>
        <p className="text-sm text-gray-400 mb-6">NetGSM hesap kurulumu tamamlandığında aktif edilecek.</p>
        <span className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-700 text-sm font-semibold px-4 py-2 rounded-full">
          <MessageSquare className="h-4 w-4" />
          Yakında
        </span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!isSmsAllowed) {
    return (
      <div className="max-w-2xl mx-auto py-8 px-4">
        <div className="flex items-center gap-3 mb-6">
          <MessageSquare className="h-6 w-6 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('settings.sms.title')}</h1>
            <p className="text-sm text-gray-500">{t('settings.sms.subtitle')}</p>
          </div>
          <span className="ml-auto text-xs bg-green-100 text-green-700 font-semibold px-2.5 py-1 rounded-full">
            {t('settings.sms.proBadge')}
          </span>
        </div>
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-700" dangerouslySetInnerHTML={{ __html: t('settings.sms.upgradeWarning') }} />
            <Link to="/billing" className="text-sm text-amber-600 underline mt-1 inline-block font-medium">
              {t('settings.sms.upgradeLink')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const usedPct = smsMonthlyLimit > 0
    ? Math.min(100, Math.round(((smsConfig.usedThisMonth || 0) / smsMonthlyLimit) * 100))
    : 0;

  return (
    <div className="max-w-2xl mx-auto py-8 px-4 space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Başlık */}
      <div className="flex items-center gap-3">
        <MessageSquare className="h-6 w-6 text-blue-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('settings.sms.title')}</h1>
          <p className="text-sm text-gray-500">{t('settings.sms.subtitle')}</p>
        </div>
        <span className="ml-auto text-xs bg-green-100 text-green-700 font-semibold px-2.5 py-1 rounded-full">
          {t('settings.sms.proBadge')}
        </span>
      </div>

      {/* Bağlantı & Durum */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-800">{t('settings.sms.isActiveLabel')}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {smsConfig.isActive ? t('settings.sms.isActiveOn') : t('settings.sms.isActiveOff')}
            </p>
          </div>
          <button
            onClick={() => setSmsConfig((p) => ({ ...p, isActive: !p.isActive }))}
            className="flex-shrink-0"
            aria-label="SMS toggle"
          >
            {smsConfig.isActive
              ? <ToggleRight className="h-9 w-9 text-blue-600" />
              : <ToggleLeft className="h-9 w-9 text-gray-400" />
            }
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.sms.apiUserLabel')}</label>
          <input
            type="text"
            value={smsConfig.apiUser}
            onChange={(e) => setSmsConfig((p) => ({ ...p, apiUser: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="netgsm_kullanici_adi"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.sms.apiKeyLabel')}</label>
          <input
            type="password"
            value={smsConfig.apiKey}
            onChange={(e) => setSmsConfig((p) => ({ ...p, apiKey: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="••••••••"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('settings.sms.senderTitleLabel')}
            <span className="ml-1 text-xs text-gray-400">({smsConfig.senderTitle.length}/11)</span>
          </label>
          <input
            type="text"
            maxLength={11}
            value={smsConfig.senderTitle}
            onChange={(e) => setSmsConfig((p) => ({ ...p, senderTitle: e.target.value.slice(0, 11) }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="SALONADIM"
          />
          <p className="flex items-center gap-1 text-xs text-gray-400 mt-1">
            <Info className="h-3 w-3" />
            {t('settings.sms.senderTitleHint')}
          </p>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleSaveConfig}
            disabled={savingConfig}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            {savingConfig ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {t('settings.sms.saveBtn')}
          </button>
        </div>
      </div>

      {/* Aylık Kullanım */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <p className="text-sm font-semibold text-gray-800 mb-3">{t('settings.sms.usageLabel')}</p>
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-gray-600">
            {smsConfig.usedThisMonth || 0}{t('settings.sms.usageOf')}{smsMonthlyLimit}{t('settings.sms.usageSuffix')}
          </span>
          <span className={`font-semibold ${usedPct >= 90 ? 'text-red-600' : usedPct >= 70 ? 'text-amber-600' : 'text-green-600'}`}>
            %{usedPct}
          </span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${usedPct >= 90 ? 'bg-red-500' : usedPct >= 70 ? 'bg-amber-500' : 'bg-green-500'}`}
            style={{ width: `${usedPct}%` }}
          />
        </div>
      </div>

      {/* SMS Şablonları */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-base font-semibold text-gray-800 mb-1">{t('settings.sms.templatesTitle')}</h2>
        <p className="text-xs text-gray-500 mb-5">{t('settings.sms.templateHint')}</p>
        <div className="space-y-4">
          {SMS_TYPES.map((type) => {
            const labelKey = `settings.sms.template${type.charAt(0).toUpperCase() + type.slice(1)}`;
            const text = smsTemplates[type] || '';
            const charCount = text.length;
            const isSaving = savingTemplate[type];
            return (
              <div key={type} className="border border-gray-100 rounded-xl p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{t(labelKey)}</span>
                  <span className={`text-xs font-mono ${charCount > 160 ? 'text-amber-600' : 'text-gray-400'}`}>
                    {charCount} {t('settings.sms.charCount')}
                  </span>
                </div>
                <textarea
                  rows={3}
                  value={text}
                  onChange={(e) => setSmsTemplates((p) => ({ ...p, [type]: e.target.value }))}
                  className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
                <div className="mt-2 flex justify-end">
                  <button
                    onClick={() => handleSaveTemplate(type)}
                    disabled={isSaving}
                    className="flex items-center gap-1.5 text-xs bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 text-gray-700 font-medium px-3 py-1.5 rounded-lg transition-colors"
                  >
                    {isSaving
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Save className="h-3.5 w-3.5" />
                    }
                    {t('settings.sms.templateSaveBtn')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bilgi Kutusu */}
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-xl p-4">
        <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="space-y-1 text-xs text-blue-700">
          <div className="flex items-center gap-1.5">
            <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
            <span>Hatırlatma — randevudan 1 saat önce otomatik gönderilir</span>
          </div>
          <div className="flex items-center gap-1.5">
            <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
            <span>Onay / İptal — randevu durumu değiştiğinde anlık gönderilir</span>
          </div>
          <div className="flex items-center gap-1.5">
            <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
            <span>Memnuniyet — randevu tamamlandıktan 2-4 saat sonra gönderilir</span>
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            <XCircle className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            <span className="text-blue-600">160 karakteri aşan mesajlar ek SMS birimi tüketir</span>
          </div>
        </div>
      </div>
    </div>
  );
}
